/**
 * components/ManageCookiePreferencesLink.tsx — reopens the consent panel
 *
 * The one-line client-component wrapper needed because /cookie-policy and
 * /privacy-policy are otherwise pure server components (per their file
 * headers — "zero client JS on the page body"). This link is the sole
 * interactive element on either page: clicking it dispatches
 * CONSENT_REOPEN_EVENT, which components/ConsentManager.tsx (mounted once,
 * site-wide, in app/layout.tsx) listens for and responds to by reopening
 * the Manage panel pre-filled with the visitor's current choice.
 *
 * Default styling: an inline text link matching the surrounding
 * .policy-body prose (inherits link color/underline from the existing
 * policy-page CSS — see .policy-body a in app/globals.css).
 *
 * `className` / `style` overrides (added 2026-07-15, 4th same-day revision —
 * Eleanor's ADD 1/ADD 2): callers outside the policy-page prose context
 * (the site footer's Legal link row, and the new top-of-cookie-policy-page
 * placement) pass their own className/style so this control visually
 * matches its surroundings instead of always rendering the gold
 * policy-body link treatment. Behavior (dispatching CONSENT_REOPEN_EVENT)
 * is identical regardless of where it's placed or how it's styled.
 *
 * Cycle: EU cookie-consent banner + Clarity install (brief 2026-07-15).
 */
'use client';

import { CONSENT_REOPEN_EVENT } from '@/lib/consent';

export default function ManageCookiePreferencesLink({
  children,
  className = 'policy-inline-link-btn',
  style,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <button
      type="button"
      className={className}
      style={style}
      onClick={() => window.dispatchEvent(new CustomEvent(CONSENT_REOPEN_EVENT))}
    >
      {children}
    </button>
  );
}
